import { upload, ApiError, type BookingAttachment, type PaymentAttachment } from '../../lib/api.ts';

export type UploadState = 'pending' | 'uploading' | 'done' | 'failed';

export interface StagedFile {
  key: string;
  file: File;
  category: string;
  description: string;
  state: UploadState;
  error?: string | null;
  uploaded?: BookingAttachment;
}

let seq = 0;
export const stage = (file: File, category = 'CHAT_SCREENSHOT'): StagedFile =>
  ({ key: `f${++seq}-${file.name}`, file, category, description: '', state: 'pending', error: null });

/** «أخرى» يتطلبها الخادم مع وصف — نتحقق محليًا حتى لا يفشل الرفع بعد الانتظار. */
export const missingDescription = (f: StagedFile): boolean =>
  f.category === 'OTHER' && !f.description.trim();

export async function uploadOne(bookingId: string, f: StagedFile, supersedesId?: string | null): Promise<BookingAttachment> {
  const form = new FormData();
  form.append('category', f.category);
  if (f.description.trim()) form.append('description', f.description.trim());
  if (supersedesId) form.append('supersedesId', supersedesId);
  form.append('file', f.file, f.file.name);
  return upload<BookingAttachment>(`/bookings/${bookingId}/communication-evidence`, form);
}

/** إثبات دفعة: بلا فئة (الأدمن يراجعه يدويًا) — الوصف اختياري ويساعد المراجعة. */
export async function uploadPaymentProof(paymentId: string, f: StagedFile, supersedesId?: string | null): Promise<PaymentAttachment> {
  const form = new FormData();
  if (f.description.trim()) form.append('description', f.description.trim());
  if (supersedesId) form.append('supersedesId', supersedesId);
  form.append('file', f.file, f.file.name);
  return upload<PaymentAttachment>(`/payments/${paymentId}/attachments`, form);
}

/**
 * رفع متسلسل (الخادم يقبل ملفًا واحدًا لكل طلب). لا يتوقف عند أول فشل:
 * كل ملف يحمل حالته الخاصة، والفاشل يبقى قابلًا لإعادة المحاولة. الحجز لا يتأثر أبدًا.
 */
export async function uploadAllWith(
  files: StagedFile[],
  uploader: (f: StagedFile) => Promise<unknown>,
  onUpdate: (f: StagedFile) => void,
): Promise<void> {
  for (const f of files) {
    if (f.state === 'done') continue;
    onUpdate({ ...f, state: 'uploading', error: null });
    try {
      const uploaded = await uploader(f);
      onUpdate({ ...f, state: 'done', error: null, uploaded: uploaded as BookingAttachment });
    } catch (err) {
      onUpdate({ ...f, state: 'failed', error: err instanceof ApiError ? err.message : 'تعذّر رفع الملف' });
    }
  }
}

export const uploadAll = (bookingId: string, files: StagedFile[], onUpdate: (f: StagedFile) => void): Promise<void> =>
  uploadAllWith(files, f => uploadOne(bookingId, f), onUpdate);
